import React, { useRef, useState } from "react";

import cropr from "../shared/utils/cropr";
import gettext from "../shared/utils/gettext";
import DashboardTile from "./DashboardTile";
import cx from "classnames";
import { createLocalStorageStateHook } from "use-local-storage-state";
import { allCropsId, useGroupedCrops } from "./hooks/useGroupedCrops";
import { ProcessingStateType, useGroupedActivities } from "./hooks/useGroupedActivities";
import { interpolate } from "../shared/utils/legacy";
import { Overlay, Popover } from "react-bootstrap";

const openRecordPage = (
  recordState: "performed" | "planned" | "adviced" | "concept",
  themeId: string,
  cropfields?: number[]
) => {
  localStorage.setItem(
    "record_stream_filter",
    JSON.stringify({ state: [recordState], theme: [themeId] })
  );
  window.open(
    "/cropr/record/record/" + (cropfields ? `#cropfields/${cropfields.join(",")}` : ""),
    "_self"
  );
};
interface ProgressPartProperties {
  titleTxt: string;
  percentage: number;
  recordState: "performed" | "planned" | "adviced" | "concept";
  activityId: string;
  allCropsId: any;
  selectedCropId: any;
  cropfieldIds: number[];
  setPopover: (open: boolean) => void;
  showPopover: boolean;
  activity: any;
}
const ProgressPart = ({
  titleTxt,
  percentage,
  recordState,
  activityId,
  allCropsId,
  selectedCropId,
  cropfieldIds,
  setPopover,
  showPopover,
  activity,
}: ProgressPartProperties) => {
  const target = useRef(null);
  const color = {
    performed: "primary",
    planned: "blue",
    adviced: "orange",
    concept: "smoke text-black",
  }[recordState];
  const popoverTitle = {
    performed: gettext("Performed records"),
    planned: gettext("Planned records"),
    adviced: gettext("Adviced records"),
    concept: gettext("Concept records"),
  }[recordState];
  return (
    <div
      ref={target}
      className={cx("progress-bar", "bg-" + color)}
      style={{ width: percentage + "%" }}
      onMouseEnter={() => setPopover(true)}
      onMouseLeave={() => setPopover(false)}
      onClick={() =>
        openRecordPage(
          recordState,
          activityId,
          selectedCropId > allCropsId ? cropfieldIds : undefined // When no crop is selected, it is not useful to pass all the cropfieldIds.
        )
      }
    >
      {titleTxt}
      {showPopover && (
        <Overlay target={target.current} show={showPopover} placement="bottom">
          <Popover
            id={"Progress" + activityId + recordState}
            placement="bottom"
            className={"summary_widget_popover"}
          >
            <Popover.Title>{popoverTitle}</Popover.Title>
            <Popover.Content className="mh-80px overflow-auto">
              {Object.entries(activity.records[recordState]).map(([activityId, activityObj]) => {
                return (
                  <div key={activityId}>
                    {/*
                     // @ts-ignore */}
                    {activityObj["label"]}: {cropr.toLocaleFixed(activityObj["area"], 2)}&nbsp;
                    {cropr.l10n.unit_area_display},{" "}
                    {/*
                     // @ts-ignore */}
                    {activityObj["count"]} {gettext("Records")}
                  </div>
                );
              })}
            </Popover.Content>
          </Popover>
        </Overlay>
      )}{" "}
    </div>
  );
};
const useRecordTileSelectedCropId = createLocalStorageStateHook(
  `RecordsTileSelectedCrop_${cropr && cropr.mainplan ? cropr.mainplan.id : ""}`,
  allCropsId
);
const RecordsTile = () => {
  const [selectedCropId, setSelectedCropId] = useRecordTileSelectedCropId();
  const [popoverOpen, setPopoverOpen] = useState("");

  const myCrops = useGroupedCrops();
  const activitiesPerCrop = useGroupedActivities(myCrops);
  const cropfields = myCrops.get(selectedCropId)?.cropfields || [];
  const cropfieldIds = cropfields.map((cf) => cf.id);
  return (
    <DashboardTile>
      <DashboardTile.Title
        right={
          <div className="dropdown">
            <button
              className="dropdown-toggle btn btn-link"
              id="dropdownMenuLink"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              {myCrops.get(selectedCropId)?.name} (
              {Math.round(myCrops.get(selectedCropId)?.area || 0)} {cropr.l10n.unit_area_display})
            </button>

            <div className="dropdown-menu" aria-labelledby="dropdownMenuLink">
              <button className="dropdown-item" onClick={() => setSelectedCropId(allCropsId)}>
                {myCrops.get(allCropsId)?.name} ({Math.round(myCrops.get(allCropsId)?.area || 0)}{" "}
                {cropr.l10n.unit_area_display})
              </button>
              <div className="dropdown-divider"></div>
              {Array.from(myCrops)
                .filter(([cropId]) => cropId > allCropsId) //Skip AllCrops here because it is hardcoded as first option
                .sort((cropA, cropB) => (cropA[1].name > cropB[1].name ? 1 : -1))
                .map(([cropId, cropItem]) => (
                  <button
                    className="dropdown-item"
                    key={cropId}
                    value={cropId}
                    onClick={() => setSelectedCropId(cropId)}
                  >
                    {cropItem.name} ({Math.round(cropItem.area)} {cropr.l10n.unit_area_display})
                  </button>
                ))}
            </div>
          </div>
        }
      >
        {gettext("Records")}
      </DashboardTile.Title>
      {/*Todo we now show this also if you have records in other themes or on cropfields without crop. Then you see this block but no bars*/}
      <DashboardTile.Body>
        {Object.entries(activitiesPerCrop[selectedCropId]).map(([activityId, activity]) => {
          /*totalArea is always at least the area for this crop*/
          const totalArea =
            activity.performedArea +
            activity.plannedArea +
            activity.advicedArea +
            activity.conceptArea;

          /*Calculate percentages. Makes sure the percentages are never larger than 100. */
          let progressbars = {};
          const acs: ProcessingStateType[] = ["planned", "performed", "adviced", "concept"];
          acs.forEach((processingState) => {
            const activityKey = processingState + "Area";
            // @ts-ignore
            const area = activity[activityKey];
            const percentage = Math.min(100, Math.round((area / totalArea) * 100)) || 0;
            const text = interpolate("%s %s", [Math.round(area), cropr.l10n.unit_area_display]);
            // @ts-ignore
            progressbars[processingState] = {
              percentage: percentage,
              text: text,
            };
          });
          return (
            <div key={selectedCropId + activityId} className="row py-2">
              <div className="col-4">
                <span className={activity.inConfig ? "" : "text-muted"}>
                  {cropr.theme[activityId].name}&nbsp;
                </span>
              </div>
              <div className={cx("col-8", "text-center", activity.inConfig ? "" : "d-hover")}>
                {(activity.inConfig && (
                  <>
                    {!totalArea && cropr.features.planning && (
                      <a
                        href={
                          "/cropr/record/record/#new_planning/" +
                          JSON.stringify({
                            theme: activityId,
                            cropfields: selectedCropId > allCropsId ? cropfieldIds : undefined, // When no crop is selected, it is not useful to pass all the cropfieldIds.
                            record_date: { day: 999 }, //invalid date to clear the date field in the modal
                          })
                        }
                        className="js-log-metric"
                        data-metrics_category="dashboard_links"
                      >
                        + {gettext("planning")}
                      </a>
                    )}
                    {totalArea > 0 && (
                      <div className="progress cursor-pointer">
                        <>
                          {acs.map((processingState, index) => {
                            // @ts-ignore
                            const progressbar = progressbars[processingState];
                            return (
                              progressbar["percentage"] > 0 && (
                                <ProgressPart
                                  titleTxt={progressbar["text"]}
                                  percentage={progressbar["percentage"]}
                                  recordState={processingState}
                                  activityId={activityId}
                                  allCropsId={allCropsId}
                                  selectedCropId={selectedCropId}
                                  cropfieldIds={cropfieldIds}
                                  setPopover={(open) =>
                                    setPopoverOpen(open ? activityId + processingState : "")
                                  }
                                  showPopover={popoverOpen === activityId + processingState}
                                  activity={activity}
                                  key={`${activityId}_${index}`}
                                />
                              )
                            );
                          })}
                        </>
                      </div>
                    )}
                  </>
                )) || (
                  <div className="text-center">
                    <a href="/cropr/settings/configuration/#recording">{gettext("Configure")}</a>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </DashboardTile.Body>
    </DashboardTile>
  );
};
export default RecordsTile;
